import { Stack, Text, type TextProps } from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import type { EvalBidDto, EventValueDto } from "../../../autogen/bff-api";
import { formatMoney } from "../../../common/formatting/displaySum";

export const showsChangeSincePrevious = (bids: EvalBidDto[]): boolean =>
  bids.some(
    (bid) => bid.previousPrice?.value != null && bid.previousPrice.value !== bid.originalPrice?.value,
  );

export const PriceChange = ({
  from,
  to,
  ...textProps
}: { from: EventValueDto | null | undefined; to: EventValueDto | null | undefined } & TextProps) => {
  const { t } = useTranslation();
  const before = from?.value;
  const after = to?.value;
  if (before == null || after == null || before === 0) {
    return (
      <Text fontSize="sm" color="smMuted" {...textProps}>
        {t("n/a")}
      </Text>
    );
  }
  const change = after - before;
  const sign = change > 0 ? "+" : "";
  const percent = (change / before) * 100;
  const currency = to?.currency ? ` ${to.currency}` : "";
  const color = change > 0 ? "smError" : change < 0 ? "smSuccess" : "smMuted";
  return (
    <Text fontSize="sm" color={color} fontFamily="mono" {...textProps}>
      {`${sign}${percent.toFixed(1)}% (${sign}${formatMoney(change)}${currency})`}
    </Text>
  );
};

export const PriceChangeLines = ({ bid, showPrevious }: { bid: EvalBidDto; showPrevious: boolean }) => {
  const { t } = useTranslation();
  if (bid.originalPrice == null && bid.previousPrice == null) return null;
  return (
    <Stack spacing="0" align="center">
      {showPrevious && (
        <Text fontSize="2xs" color="smMuted">
          {t("Since previous bid")}:{" "}
          <PriceChange as="span" fontSize="2xs" from={bid.previousPrice} to={bid.effectivePrice} />
        </Text>
      )}
      <Text fontSize="2xs" color="smMuted">
        {t("Since original bid")}: <PriceChange as="span" fontSize="2xs" from={bid.originalPrice} to={bid.effectivePrice} />
      </Text>
    </Stack>
  );
};
